import { useState, useEffect } from "react";
import { Joyride, STATUS } from "react-joyride";

const TOUR_KEY = "dynastydeal_tour_complete";

const tourSteps = [
  {
    target: '[data-testid="nav-logo"]',
    content: "Welcome to Dynasty Trade Maker! Let's take a quick tour of the key features.",
    placement: "bottom",
    disableBeacon: true,
  },
  {
    target: '[data-testid="league-selector"]',
    content: "Switch between your imported Sleeper leagues here. All data auto-syncs with FantasyCalc values.",
    placement: "bottom",
  },
  {
    target: '[data-testid="stat-total-value"]',
    content: "Your total dynasty value — players + draft picks combined. This drives your power ranking across the league.",
    placement: "bottom",
  },
  {
    target: '[data-testid="nav-group-trades"]',
    content: "The Trades menu has 6 tools: AI Analyzer for smart suggestions, Finder to auto-scan trades, Simulator for before/after impact, Calculator, Evaluator, and your saved Inbox.",
    placement: "bottom",
  },
  {
    target: '[data-testid="nav-group-league"]',
    content: "See all teams with positional rankings, contender/rebuilder scores, and multi-season history.",
    placement: "bottom",
  },
  {
    target: '[data-testid="nav-group-draft"]',
    content: "Draft War Room gives you pick-by-pick AI advice, and Mock Draft simulates the full draft based on team needs.",
    placement: "bottom",
  },
  {
    target: '[data-testid="analyze-roster-btn"]',
    content: "Click here for an AI analysis of your roster — strengths, weaknesses, and strategic recommendations.",
    placement: "left",
    optional: true,
  },
  {
    target: '[data-testid="go-trade-btn"]',
    content: "Ready to find trades? Jump to the AI Trade Analyzer to get started!",
    placement: "left",
    optional: true,
  },
];

const joyrideStyles = {
  options: {
    zIndex: 10000,
    primaryColor: "#ccff00",
    backgroundColor: "#171717",
    textColor: "#ffffff",
    arrowColor: "#171717",
    overlayColor: "rgba(0, 0, 0, 0.7)",
  },
  tooltipContainer: {
    textAlign: "left",
  },
  tooltip: {
    backgroundColor: "#171717",
    border: "1px solid #262626",
    borderRadius: "2px",
    padding: "16px",
    fontFamily: "Manrope, sans-serif",
  },
  tooltipContent: {
    padding: "8px 0",
    fontSize: "13px",
    lineHeight: "1.5",
    color: "#a3a3a3",
  },
  tooltipTitle: {
    fontSize: "14px",
    fontWeight: 700,
    color: "#ffffff",
    fontFamily: "Bebas Neue, sans-serif",
  },
  buttonNext: {
    backgroundColor: "#ccff00",
    color: "#000000",
    fontSize: "11px",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    borderRadius: "2px",
    padding: "8px 16px",
  },
  buttonBack: {
    color: "#a3a3a3",
    fontSize: "11px",
    fontWeight: 600,
    textTransform: "uppercase",
  },
  buttonSkip: {
    color: "#666",
    fontSize: "11px",
  },
  buttonClose: {
    color: "#666",
  },
  beacon: {
    display: "none",
  },
};

export default function OnboardingTour({ run }) {
  const [shouldRun, setShouldRun] = useState(false);

  useEffect(() => {
    if (!run) return;
    const done = localStorage.getItem(TOUR_KEY);
    if (!done) {
      // Small delay to let the dashboard render
      const timer = setTimeout(() => setShouldRun(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [run]);

  const handleCallback = (data) => {
    const { status } = data;
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      localStorage.setItem(TOUR_KEY, "true");
      setShouldRun(false);
    }
  };

  // Filter steps to only those with targets that exist in the DOM
  const [activeSteps, setActiveSteps] = useState([]);
  useEffect(() => {
    if (!shouldRun) return;
    const available = tourSteps.filter(step => {
      try {
        return !!document.querySelector(step.target);
      } catch {
        return false;
      }
    });
    setActiveSteps(available);
  }, [shouldRun]);

  if (!shouldRun || activeSteps.length === 0) return null;

  return (
    <Joyride
      steps={activeSteps}
      run={shouldRun}
      continuous
      showSkipButton
      showProgress
      callback={handleCallback}
      styles={joyrideStyles}
      locale={{
        back: "BACK",
        close: "CLOSE",
        last: "DONE",
        next: "NEXT",
        skip: "SKIP TOUR",
      }}
      floaterProps={{
        disableAnimation: true,
      }}
    />
  );
}
